import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { forwardRef } from 'react'
import { Spinner } from './Spinner'

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger'
export type ButtonSize = 'sm' | 'md' | 'lg'

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  /** Full width. The default on phones for anything primary. */
  block?: boolean
  /** Shows a spinner and blocks input without changing the button's width. */
  busy?: boolean
  /** Leading glyph, e.g. `<SearchIcon />`. */
  icon?: ReactNode
}

/**
 * The only button. 44px minimum height everywhere, including the small variant — "small"
 * changes padding and type size, never the touch target.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'secondary',
    size = 'md',
    block = false,
    busy = false,
    icon,
    className,
    children,
    disabled,
    type = 'button',
    ...rest
  },
  ref,
) {
  const classes = [
    'rs-btn',
    `rs-btn--${variant}`,
    size !== 'md' ? `rs-btn--${size}` : '',
    block ? 'rs-btn--block' : '',
    busy ? 'rs-btn--busy' : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <button
      ref={ref}
      type={type}
      className={classes}
      disabled={disabled || busy}
      aria-busy={busy || undefined}
      {...rest}
    >
      {busy ? <Spinner size="sm" /> : icon}
      {children}
    </button>
  )
})
